import { useId, type ComponentType, type SVGProps } from 'react';
import {
    ArrowDownTrayIcon,
    ComputerDesktopIcon,
    CubeIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import type { IteroAssetType } from '../../../../../services/iteroBrowse';
import { Spinner } from '../BrowserPrimitives';
import { CARD_CLASS, SECTION_HEADING_CLASS } from '../browserStyles';
import type { AssetDownload } from '../order-view/useOrderAssetDownloads';
import { fileRowView, type FileRowTone } from './fileRows';
import type { ImageTally } from './scanTally';

const ROW_ICONS: Readonly<Record<IteroAssetType, ComponentType<SVGProps<SVGSVGElement>>>> = {
    IDE: CubeIcon,
    IMAGE_GALLERY: PhotoIcon,
    SCREEN_CAPTURE: ComputerDesktopIcon
};

const NOTE_TONE_CLASS: Readonly<Record<FileRowTone, string>> = {
    muted: 'text-gray-500',
    success: 'text-green-700',
    warning: 'text-amber-700',
    error: 'text-red-600'
};

type FileTallies = Partial<Record<IteroAssetType, ImageTally>>;

function FileRow({ download, tally }: { download: AssetDownload; tally: ImageTally | null }) {
    const row = fileRowView(download.option, download.state, tally);
    const Icon = ROW_ICONS[download.option.type];
    return (
        <li className="flex items-center gap-3 px-3.5 py-3">
            <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-600">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[13px] font-semibold leading-[18px] text-gray-900">
                    {row.title}
                </span>
                <span className="truncate text-[12px] leading-4 text-gray-500">{row.subline}</span>
                {/* Always laid out, so a note appearing never moves the rows below. */}
                <span
                    aria-live="polite"
                    title={row.note?.text}
                    className={`h-4 truncate text-[11px] leading-4 ${row.note ? NOTE_TONE_CLASS[row.note.tone] : ''}`}
                >
                    {row.note?.text}
                </span>
            </div>
            <button
                type="button"
                onClick={download.start}
                disabled={!row.canDownload}
                aria-busy={row.isBusy}
                aria-label={`Download ${row.title.toLowerCase()}`}
                className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${
                    row.isBusy ? 'cursor-wait' : 'disabled:cursor-not-allowed disabled:opacity-40'
                }`}
            >
                {row.isBusy ? (
                    <Spinner className="h-3.5 w-3.5" />
                ) : (
                    <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                )}
            </button>
        </li>
    );
}

interface ScanFilesCardProps {
    downloads: readonly AssetDownload[];
    tallies: FileTallies;
}

export function ScanFilesCard({ downloads, tallies }: ScanFilesCardProps) {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId} className={CARD_CLASS}>
            <h5 id={headingId} className={`${SECTION_HEADING_CLASS} px-3.5 pb-0.5 pt-3`}>
                Files
            </h5>
            <ul className="divide-y divide-gray-100">
                {downloads.map((download) => (
                    <FileRow
                        key={download.option.type}
                        download={download}
                        tally={tallies[download.option.type] ?? null}
                    />
                ))}
            </ul>
        </section>
    );
}
